import { toast } from 'react-toastify';
import {
  createRecord,
  handleUpdateStatus,
  handleUpdateError,
  getReport,
  checkIfOnline,
} from '@/api/imperative/records';
import { getSignedUrl, uploadToS3 } from '@/api/imperative/uploads';
import { createRecordingLog } from '@/api/imperative/recording';
import { getFreshToken } from '@/shared/lib/appHelpers'
import { handleLogout } from '@/features/auth/lib/authSession'
import { persistToolFlags, resolveToolFlags, toCreateRecordFlags } from '@/shared/lib/toolFormFlags'
import { SESSION_EXPIRED_ERROR } from '@/shared/config/urls'
import { deleteDraft } from '@/shared/lib/draftService'

export async function runGeneratePipeline({
  data,
  toolId,
  toolName,
  integration,
  newReportId,
  recordingStarted,
  recordingDuration,
  recordedBlob,
  selectedFile,
  documentInfo,
  setDocumentInfo,
  dispatch,
  setRecordingStarted,
  setRecordingDuration,
  setReportId,
  setFileUploading,
  setIntegration,
  setGenerating,
  retrySubmit,
  clearGenerateStates,
  handleResetState,
  reportIdRef,
}) {
  const {
    toolFieldsData,
    reportLanguage,
    audioLanguage,
    comment,
    template,
    uses_custom_template,
    urgent,
    enableTextModules,
    activateICDCodes,
    isPrivate,
    recordedAudioBlob,
    draftId
  } = data;

  dispatch(setFileUploading(true));
  
  if (recordingStarted && newReportId) {
    createRecordingLog({
      type: 'generate',
      duration: recordingDuration,
      reportId: newReportId,
      createdAt: new Date().toISOString()
    });
    dispatch(setRecordingStarted(false));
    dispatch(setRecordingDuration(0));
  }

  const userId = localStorage.getItem('user_id');
  let mediaFile = recordedAudioBlob || recordedBlob || selectedFile;

  if (!mediaFile) {
    toast.error('No media file available for processing.');
    retrySubmit();
    return;
  }

  const isMultipleFiles = Array.isArray(mediaFile);
  const files = isMultipleFiles ? mediaFile : [mediaFile];

  toolFieldsData.forEach((field, index) => {
    if (index > 0) {
      localStorage.setItem(field.name, field.value);
    }
  });
  localStorage.setItem('audio_language', audioLanguage);
  localStorage.setItem('report_language', reportLanguage);
  localStorage.setItem('uses_custom_template', uses_custom_template);
  const toolFlags = resolveToolFlags(toolName, { enableTextModules, activateICDCodes });
  persistToolFlags(toolName, toolFlags);

  let { documentId, rowNumber, signedUrl, audioUploaded, statusUpdated } = documentInfo;

  if (!documentId) {
    const document = await createRecord({
      reportId: newReportId,
      userId,
      toolFieldsData,
      reportLanguage,
      uses_custom_template,
      audioLanguage,
      comment,
      template,
      meta_type: files[0]?.type,
      toolId,
      urgent,
      integration,
      ...toCreateRecordFlags(toolFlags),
      isPrivate
    });
    documentId = document?.documentId;
    rowNumber = document?.row_number;
    if (!documentId) {
      retrySubmit();
      return;
    }
    setDocumentInfo((prevState) => ({ ...prevState, documentId, rowNumber }));
  }

  dispatch(setReportId(null));

  if (isMultipleFiles) {
    const signedUrlPromises = files.map((file) => 
      getSignedUrl({ 
        id: documentId, 
        name: `${documentId}/${file.name}`, 
        type: "upload",
        meta_type: file.type 
      })
    );

    const signedUrls = await Promise.all(signedUrlPromises);
    
    if (signedUrls.some(url => !url)) {
      retrySubmit();
      return;
    }

    const uploadPromises = files.map((file, index) => 
      uploadToS3({ 
        id: documentId, 
        signedUrl: signedUrls[index], 
        file 
      })
    );

    const [uploadResults, isOnline] = await Promise.all([
      Promise.all(uploadPromises),
      checkIfOnline()
    ]);

    if (!isOnline || uploadResults.some(result => !result)) {
      retrySubmit();
      return;
    }
  } else {
    if (!signedUrl) {
      signedUrl = await getSignedUrl({ id: documentId, name: documentId, type: "upload" });
      if (!signedUrl) {
        retrySubmit();
        return;
      }
      setDocumentInfo((prevState) => ({ ...prevState, signedUrl }));
    }

    if (!audioUploaded) {
      const [uploadStatus, isOnline] = await Promise.all([
        uploadToS3({ id: documentId, signedUrl, file: mediaFile }),
        checkIfOnline()
      ]);

      if (!isOnline || !uploadStatus) {
        setDocumentInfo((prevState) => ({ ...prevState, signedUrl: '' }));
        retrySubmit();
        return;
      }
      setDocumentInfo((prevState) => ({ ...prevState, audioUploaded: true }));
    }
  }

  if (!statusUpdated) {
    const recordUpdateStatus = await handleUpdateStatus({ status: 'processing', documentId });
    if (!recordUpdateStatus) {
      retrySubmit();
      return;
    }
    setDocumentInfo((prevState) => ({ ...prevState, statusUpdated: true }));
  }

  let token;
  try {
    token = await getFreshToken();
  } catch (error) {
    retrySubmit();
    return;
  }

  const { response, responseData } = await getReport(documentId, token);

  dispatch(setFileUploading(false));
  setGenerating(true);
  dispatch(setIntegration('reset'));

  if (responseData.statusCode !== 200) {
    if (response.status === 401) {
      handleUpdateError({ id: documentId, error: SESSION_EXPIRED_ERROR });
      handleLogout();
      toast.error('Your session has expired. Please login and try again.');
      return;
    } else {
      clearGenerateStates();
      handleResetState();
      handleUpdateError({ id: documentId, error: JSON.stringify(responseData) });
      return;
    }
  }

  reportIdRef.current = documentId;
  clearGenerateStates();

  if (draftId) {
    deleteDraft(draftId).catch((err) =>
      console.error('Failed to delete draft after success:', err)
    );
  }
}
